// Packets Need (Daily Planning tab): the balance chain of one packet, the suggested production
// batches and the operator load. Pure functions — PacketsNeedTab.jsx only lays them out in columns.
// Formulas and examples: server/docs/report-small-packets-need.md

const num = x => Number(x) || 0;
const weekday = d => new Date(d + 'T12:00:00Z').getUTCDay();   // 0 = Sunday … 6 = Saturday

// One packet, day by day. dates: every calendar day from today to the end of the range, in order.
//   need / planned  { 'YYYY-MM-DD': qty }
//   todayLeft       today's need still to be taken from stock (sets packed today have already
//                   written their packets off); undefined = nothing is planned today
//   monthNeed       the packet's need of the month that starts today (30 or 31 days, from the server) — picks the batch
// balance is the real one: stock + planned production − need. A batch (`produce`) is suggested on a
// day the packet is taken and its balance is below the red limit: a whole number of the packet's own
// batches (3,000 or 6,000), enough to cover what is below zero, never rounded down, at least one batch.
// Batches never enter `balance`, they only make sure one shortage is not counted on every following day. inPlan[d] = the packet has been taken at least once from today to d:
// from that day on it is "in production" and its low balance is shown and counted.
export function packetChain({ stock, need = {}, planned = {}, todayLeft, monthNeed, dates, settings: s }) {
  const balance = {}, produce = {}, inPlan = {};
  let taken = false;
  const min = num(monthNeed) > s.batchThreshold ? s.batchLarge : s.batchSmall;
  let bal = Math.max(num(stock), 0), made = 0;
  dates.forEach((d, i) => {
    const n = num(need[d]);
    bal += num(planned[d]) - (i === 0 && todayLeft != null ? num(todayLeft) : n);
    balance[d] = bal;
    if (n > 0) taken = true;
    inPlan[d] = taken;
    if (n > 0 && bal + made < s.redBelow) {
      const missing = Math.max(0, -(bal + made));                       // what is below zero
      produce[d] = Math.max(1, Math.ceil(missing / min)) * min;         // 7,000 → 2 × 6,000; 13,000 → 3 × 6,000
      made += produce[d];
    }
  });
  return { balance, produce, inPlan };
}

// 'alert' (below redBelow) | 'warn' (up to yellowMax) | ''
export const level = (bal, s) => (bal == null ? '' : bal < s.redBelow ? 'alert' : bal <= s.yellowMax ? 'warn' : '');

// One packet over one column — a day, an ISO-week segment or a month (dates = the column's days).
// need = Σ of the days, produce = Σ of the batches suggested in the period, bal = balance at the end of the period (its last day).
// lv = the colour by that end balance, for a packet that is in the plan by then (taken at least once
// from today to the end of the period). So the last week of a month and the month itself always agree,
// and a packet nobody takes stays white whatever its balance is.
export function periodCell(row, dates, s) {
  let need = 0, produce = 0, bal = null, inPlan = false;
  for (const d of dates) {
    need += num(row.need[d]);
    produce += num(row.chain.produce[d]);
    if (row.chain.balance[d] !== undefined) { bal = row.chain.balance[d]; inPlan = row.chain.inPlan[d]; }
  }
  return { need, produce, bal, lv: inPlan ? level(bal, s) : '' };
}

// Totals of one column over the given packets. short = the packets whose cell is red in this column
// (in the plan by the end of the period, end balance below the red limit); load = suggested batches + production
// already planned. Working days = the weekdays listed in settings.workDays.
export function periodSummary(rows, dates, s) {
  let need = 0, short = 0, produce = 0, planned = 0;
  for (const r of rows) {
    const x = periodCell(r, dates, s);
    need += x.need;
    produce += x.produce;
    if (x.lv === 'alert') short += 1;
    for (const d of dates) planned += num(r.planned[d]);
  }
  const load = produce + planned;
  const workdays = Math.max(dates.filter(d => s.workDays.includes(weekday(d))).length, 1);   // days off only → 1: any load there is over the capacity
  return {
    need, short, produce, planned, load, workdays,
    shiftsShort: load / s.capShort,
    shiftsLong: load / s.capLong,
    longer: load > s.capShort * workdays,      // the short day is not enough → ask to work until 6 PM
    overload: load > s.capLong * workdays,     // even the long day is not enough → one operator cannot make it
  };
}
